import { EventCategory } from '@kinetic-ui/data-layer';
import {
  AppSearchItemType,
  DEFAULT_ZOOM,
  MapContext,
  MapSearchResultItem,
  MapSearchResultProps,
  publicUrl,
  useAppSearchHistory,
  useKineticMap,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
} from '@kinetic-ui/shared';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ENTITY_DRAWER_VIEW_ALL_ROUTE,
  ENTITY_EDIT_ROUTE,
  ENTITY_TEMPLATE_EDIT_ROUTE,
  ENTITY_RESOURCE_PLAN_EDIT_ROUTE,
} from '../EntityList/consts';
import { getAppSearchIconKey, renderIcon } from './utils';
import { useCombinedResults } from './useCombinedResults';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { searchText } = useMapBoxSearchContext();
  const { push } = useHistory();
  const map = useKineticMap();
  const { addSearchHistory } = useAppSearchHistory();

  const combinedResults = useCombinedResults(searchText, isAppSearch ?? false);

  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const event = combinedResults.find((e) => e.id === id);
        let route = ENTITY_EDIT_ROUTE;

        if (event?.__typename === 'EventTemplate') {
          route = ENTITY_TEMPLATE_EDIT_ROUTE;
        } else if (event?.__typename === 'ResourcePlan') {
          route = ENTITY_RESOURCE_PLAN_EDIT_ROUTE;
        }

        const path = `/${route}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        if (event?.name) {
          addSearchHistory({
            name: event.name,
            link: path,
            type: AppSearchItemType.View,
            icon: getAppSearchIconKey(event.category),
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }
      const layersToAdd: string[] = [];
      Object.entries(EventCategory).forEach((category) => {
        if (category[1] === EventCategory.ScheduledEvent) {
          return;
        }
        !selectedLayers.includes(category[1]) && layersToAdd.push(category[1]);
      });

      layersToAdd.length > 0 && setSelectedLayers([...selectedLayers, ...layersToAdd]);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);

      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [
      isAppSearch,
      hasEntityDrawer,
      setSelectedLayers,
      selectedLayers,
      push,
      map,
      combinedResults,
      onResultClick,
      addSearchHistory,
      addMapPin,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {combinedResults.map((event) => (
        <MapSearchResultItem
          key={`searchItem_${event.id}`}
          name={event.name ?? ''}
          onClick={(openInNewWindow) => onClick(event.id, event.geometry, openInNewWindow)}
          icon={renderIcon(event.category)}
          newWindow={isAppSearch}
          saveToHistory={!isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
